<script setup lang="ts">
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { UserState } from '@/composition/users'
import { handleBackButton } from '@/helper/router.helper'
import { UsersService } from '@/service/UsersService'
import { useGlobalStore } from '@/store/app'
import { hidePageLoaderAnimation } from '@/util/helper'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { t } = useI18n()

const UIConfirmation = defineAsyncComponent(
  () => import('@/components/common/modal/UIConfirmation.vue')
)

const MandatedInfoModal = defineAsyncComponent(
  () => import('@/components/common/MandatedInfoModal.vue')
)
const Follow = defineAsyncComponent(
  () => import('@/components/Follow/Follow.vue')
)
const LoginModal = defineAsyncComponent(
  () => import('@/components/login/LoginModal.vue')
)

const GroupOwned = defineAsyncComponent(
  () => import('@/components/globalProfile/GroupOwned.vue')
)
const MembershipGroup = defineAsyncComponent(
  () => import('@/components/globalProfile/MembershipGroup.vue')
)
const Contributions = defineAsyncComponent(
  () => import('@/components/globalProfile/Contributions.vue')
)
const ProfileCard = defineAsyncComponent(
  () => import('@/components/globalProfile/ProfileCard.vue')
)
const BackButton = defineAsyncComponent(
  () => import('@/components/common/actionButtons/BackButton.vue')
)
const appStore = useGlobalStore()

const router = useRouter()
const route = useRoute()

const showMandatedInfoModal = computed(
  () => UserState.state.showMandatoryInfoModal
)
const currentTab = computed<string>(() => {
  return router.currentRoute.value.query.tab as string
})

/**
 * User details
 */
const userDetails = ref<any>({})
const _props = defineProps({
  userSlug: { type: String, required: true },
})

const showLoginModal = computed(() => appStore.showLoginModal)
const loginPage = computed(() => appStore.loginPage)
const isLoading = ref(true)
const userNotFound = ref(false)
const fetchUserDetails = async () => {
  try {
    const userDetailsResponse = await UserState.getUserDetails(_props.userSlug)
    if (!userDetailsResponse.contactId) {
      userNotFound.value = true
      return
    }
    userDetails.value = userDetailsResponse
  } catch (_error) {
    userNotFound.value = true
  }
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'instant' as ScrollBehavior,
  })
}

watch(
  () => _props.userSlug,
  async (currSlug, oldSlug) => {
    if (currSlug !== oldSlug) {
      scrollToTop()
      try {
        isLoading.value = true
        await Promise.all([
          fetchUserDetails(),
          GroupState.getGroupByLocation({ limit: 15 }),
        ])
      } catch (error) {
        console.error('Error loading global user profile:', error)
      } finally {
        isLoading.value = userNotFound.value
      }
    }
  },
  { immediate: true }
)

onMounted(async () => {
  hidePageLoaderAnimation()
})

const onUpdateSelectedGroup = async () => {
  const userGroupInfo = await UsersService.getUserGroups(
    userDetails.value?.contactId
  )
  userDetails.value.userGroupData = userGroupInfo
}

const handleCancelModalForUserNotFound = (event: any) => {
  if (event) {
    return
  }
  userNotFound.value = false
  handleBackButton(router, route)
}
</script>

<template>
  <div
    class="mt-12 min-h-screen bg-communities-surface"
    :class="{
      'mt-24': app.state.isMobile && !appStore.isAuthenticated && !isLoading,
    }"
  >
    <div
      class="flex min-h-screen items-start justify-center md:py-12"
      :class="{ 'py-8': !app.state.isMobile }"
    >
      <div
        class="mx-auto flex w-full max-w-[1400px] flex-col justify-center gap-6 md:px-6 lg:flex-row lg:gap-6 xl:gap-8"
        :class="{ 'px-4': !app.state.isMobile }"
      >
        <!-- Sidebar - shows first on mobile, second on desktop -->
        <div
          class="order-1 w-[364px] lg:sticky lg:top-8 lg:order-2"
          :class="{ 'w-full': app.state.isMobile }"
        >
          <ProfileCard :profile="userDetails" :is-loading="isLoading" />
        </div>

        <div
          v-if="currentTab && ['followers', 'following'].includes(currentTab)"
          class="order-2 flex w-full max-w-[722px] flex-col gap-0 lg:order-1"
        >
          <Follow
            :contact-id="userDetails?.contactId"
            :is-loading="isLoading"
            :following-count="userDetails?.followingCount"
            :follower-count="userDetails?.followersCount"
            :current-tab="currentTab"
          />
        </div>
        <!-- Main content - shows second on mobile, first on desktop -->
        <div
          v-else
          class="order-2 flex w-full max-w-[722px] flex-col gap-0 lg:order-1"
        >
          <BackButton v-if="!app.state.isMobile" class="mb-4" />
          <GroupOwned :user="userDetails" :is-user-info-loading="isLoading" />

          <MembershipGroup
            :is-user-info-loading="isLoading"
            :contact-id="userDetails?.contactId"
          />

          <Contributions
            :is-user-info-loading="isLoading"
            :contact-id="userDetails?.contactId"
            @update:selected-group="onUpdateSelectedGroup"
          />
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="userNotFound"
    class="modal-parent sticky top-0 flex w-full select-none overflow-hidden"
  >
    <UIConfirmation
      :show="userNotFound"
      :show-actions="false"
      type="error"
      :title="t('common.userNotFoundMessage')"
      button-type="error"
      message="The user you are trying to view does not exist."
      @update:show="e => handleCancelModalForUserNotFound(e)"
    />
  </div>
  <LoginModal
    v-if="showLoginModal"
    :page="loginPage"
    :location-data="app.state.locationData"
    :show="showLoginModal"
    :login-confirmation="() => appStore.setShowLoginModal(false)"
    @update:show="() => appStore.closeLoginModal()"
  />
  <MandatedInfoModal v-if="showMandatedInfoModal" :show-modal="true" />
</template>

<style scoped>
/* All styles now converted to Tailwind CSS utilities */
</style>
